import { DatePipe } from '@angular/common';
import { CommitteeAuditLog } from 'lfx-pcc';
import { COMMITTEE_LOGS_FIELD_MAPPING } from '@app/shared/utils/activity-logs';
import { escapeHtml, isValidUrlWithAutoProtocol, linkify, prependHttp, truncateText, validateEmail } from '@app/shared/utils/helper';
import { DESCRIPTION_TRUNCATE_LENGTH, HIDE_FIELDS } from '../constants/committee-audit-log.constants';
import {
  AuditUserAlternateAvatars,
  CommitteeAuditEntry,
  CommitteeAuditProcessingResult,
  CommitteeFilterState,
  FilterUser,
  GroupedAuditLog,
} from '../interfaces/committee-audit-log.interface';

// ── Pure helper functions ──────────────────────────────────────────────

export function getActionCssClass(action: string): string {
  const actionLower = action.toLowerCase();
  if (actionLower.includes('create') || actionLower.includes('add')) return 'action-pill-created';
  if (actionLower.includes('delete') || actionLower.includes('remove')) return 'action-pill-removed';
  return 'action-pill-updated';
}

export function getFieldName(field: string): string {
  return COMMITTEE_LOGS_FIELD_MAPPING.find((option) => option.field === field)?.value || field.trim();
}

export function formatData(data: string): string {
  if (data === 'nil') return `-`;
  if (validateEmail(data)) return `<a class="text-primary" href="mailto:${escapeHtml(data)}">${escapeHtml(data)}</a>`;
  if (isValidUrlWithAutoProtocol(data)) {
    return `<a class="text-primary" href="${prependHttp(data)}" target="_blank" rel="noopener noreferrer">${escapeHtml(data)}</a>`;
  }
  return escapeHtml(data);
}

// ── Main processing ────────────────────────────────────────────────────

export function processTableData(auditLogs: CommitteeAuditLog[]): CommitteeAuditProcessingResult {
  const auditLogTableData: CommitteeAuditEntry[] = [];
  const userMap = new Map<string, FilterUser>();
  const typeSet = new Set<string>();
  const actionSet = new Set<string>();
  const committeeSet = new Set<string>();
  const memberSet = new Set<string>();
  const fieldSet = new Set<string>();

  auditLogs.forEach((log: CommitteeAuditLog) => {
    const alt = log.AuditUser as unknown as AuditUserAlternateAvatars;
    const resolvedAvatar = log.AuditUser.Avatar || alt.profile_picture || alt.LogoUrl || alt.logoUrl || '';
    const rawName = log.AuditUser.Name || log.AuditUser.UserName || 'Unknown User';
    const resolvedName = rawName === 'User-Service' ? 'User System' : rawName;
    const resolvedUser = { ...log.AuditUser, Name: resolvedName, Avatar: resolvedAvatar };

    log.Changes.forEach((change) => {
      if (!HIDE_FIELDS.has(change.Field)) {
        const from = change.From?.trim() || '-';
        const to = change.To?.trim() || '-';
        const type = log.RecordType === 'COMMITTEEMEMBER' ? 'Committee Member' : 'Committee';
        const fieldName = getFieldName(change.Field) || '-';
        const isDescription = change.Field === 'description__c';

        auditLogTableData.push({
          createdAt: log.CreatedAt,
          filterDate: new Date(log.CreatedAt),
          field: fieldName,
          from: isDescription ? linkify(truncateText(from, DESCRIPTION_TRUNCATE_LENGTH), '_blank', '', 'text-primary') : formatData(from),
          to: isDescription ? linkify(truncateText(to, DESCRIPTION_TRUNCATE_LENGTH), '_blank', '', 'text-primary') : formatData(to),
          originalFrom: from,
          originalTo: to,
          auditUser: resolvedUser,
          action: log.Action,
          memberName: log.MemberName || '-',
          committeeName: log.CommitteeName || '-',
          type,
          actionCssClass: getActionCssClass(log.Action),
        });

        if (!userMap.has(resolvedUser.UserID)) {
          userMap.set(resolvedUser.UserID, {
            UserID: resolvedUser.UserID,
            displayName: resolvedName,
            Avatar: resolvedAvatar || undefined,
          });
        }

        typeSet.add(type);
        actionSet.add(log.Action);
        if (log.CommitteeName) committeeSet.add(log.CommitteeName);
        if (log.MemberName) memberSet.add(log.MemberName);
        fieldSet.add(fieldName);
      }
    });
  });

  const sortedAuditLogs = [...auditLogTableData].sort((a, b) => {
    const timeCompare = new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    if (timeCompare !== 0) return timeCompare;
    return (a.auditUser?.UserID || '').localeCompare(b.auditUser?.UserID || '');
  });

  return {
    sortedAuditLogs,
    filterUsers: Array.from(userMap.values()),
    filterTypes: Array.from(typeSet).sort(),
    filterActions: Array.from(actionSet).sort(),
    filterCommittees: Array.from(committeeSet).sort(),
    filterMembers: Array.from(memberSet).sort(),
    filterFields: Array.from(fieldSet).sort(),
  };
}

// ── Filtering ──────────────────────────────────────────────────────────

export function applyFilters(sortedAuditLogs: CommitteeAuditEntry[], filterState: CommitteeFilterState): CommitteeAuditEntry[] {
  const fromDate = filterState.dateFrom ? new Date(filterState.dateFrom).setHours(0, 0, 0, 0) : null;
  const toDate = filterState.dateTo ? new Date(filterState.dateTo).setHours(23, 59, 59, 999) : null;
  const selectedTypeSet = filterState.selectedTypes.length > 0 ? new Set(filterState.selectedTypes) : null;
  const selectedActionSet = filterState.selectedActions.length > 0 ? new Set(filterState.selectedActions) : null;
  const selectedCommitteeSet = filterState.selectedCommittees.length > 0 ? new Set(filterState.selectedCommittees) : null;
  const selectedMemberSet = filterState.selectedMembers.length > 0 ? new Set(filterState.selectedMembers) : null;
  const selectedUserIdSet = filterState.selectedUsers.length > 0 ? new Set(filterState.selectedUsers.map((u) => u.UserID)) : null;
  const selectedFieldSet = filterState.selectedFields.length > 0 ? new Set(filterState.selectedFields) : null;

  const hasActiveFilters =
    fromDate !== null ||
    toDate !== null ||
    selectedTypeSet !== null ||
    selectedActionSet !== null ||
    selectedCommitteeSet !== null ||
    selectedMemberSet !== null ||
    selectedUserIdSet !== null ||
    selectedFieldSet !== null;

  if (!hasActiveFilters) return sortedAuditLogs;

  return sortedAuditLogs.filter((log) => {
    if (fromDate !== null || toDate !== null) {
      const logTime = new Date(log.createdAt).getTime();
      if (fromDate !== null && logTime < fromDate) return false;
      if (toDate !== null && logTime > toDate) return false;
    }

    if (selectedTypeSet && log.type && !selectedTypeSet.has(log.type)) return false;
    if (selectedActionSet && log.action && !selectedActionSet.has(log.action)) return false;
    if (selectedCommitteeSet && log.committeeName && !selectedCommitteeSet.has(log.committeeName)) return false;
    if (selectedMemberSet && log.memberName && !selectedMemberSet.has(log.memberName)) return false;

    if (selectedUserIdSet) {
      const userId = log.auditUser?.UserID;
      if (!userId || !selectedUserIdSet.has(userId)) return false;
    }

    if (selectedFieldSet && log.field && !selectedFieldSet.has(log.field)) return false;

    return true;
  });
}

// ── Grouping ───────────────────────────────────────────────────────────

export function groupAuditLogs(logs: CommitteeAuditEntry[]): GroupedAuditLog[] {
  if (logs.length === 0) return [];

  const groups: GroupedAuditLog[] = [];
  let currentGroup: GroupedAuditLog | null = null;

  for (const log of logs) {
    const userId = log.auditUser?.UserID || 'unknown';
    const committeeName = log.committeeName || 'unknown';

    if (
      currentGroup &&
      currentGroup.createdAt === log.createdAt &&
      currentGroup.auditUser?.UserID === userId &&
      currentGroup.logs[0]?.committeeName === committeeName
    ) {
      currentGroup.logs.push(log);
    } else {
      currentGroup = {
        groupId: `${log.createdAt}-${userId}-${committeeName}`,
        logs: [log],
        createdAt: log.createdAt,
        auditUser: log.auditUser,
      };
      groups.push(currentGroup);
    }
  }

  return groups;
}

// ── Export helpers ──────────────────────────────────────────────────────

export function getColumnMap(datePipe: DatePipe): Record<string, (log: CommitteeAuditEntry) => string> {
  return {
    Date: (log) => datePipe.transform(log.createdAt, 'dd MMM y h:mm a') || '-',
    Committee: (log) => log.committeeName || '-',
    Type: (log) => log.type || '-',
    Action: (log) => log.action || '-',
    Members: (log) => log.memberName || '-',
    Field: (log) => log.field || '-',
    From: (log) => log.originalFrom || '-',
    To: (log) => log.originalTo || '-',
    User: (log) => log.auditUser?.Name || log.auditUser?.Email || '-',
  };
}
